import { Button, buttonVariants } from "@gigpro/ui/Button";
import { Menu, Search } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { MobileHeaderDecor } from "components/Halloween/MobileHeaderDecor";
import { MobileTopNavigation } from "components/MobileTopNavigation";
import { useSetAtom } from "jotai";
import { PanelLeft } from "lucide-react";
import { useFeatureFlag, useMarketplaceOverhaul } from "providers/LaunchDarkly";
import { drawerAtom } from "services/atoms";
import type { NavItem } from "../types";
import { useNavLinkProps } from "../utils";

type NavigationBarProps = {
  title?: string;
  topBar: Array<NavItem>;
  drawerItems: Array<NavItem>;
  onSearchBarButtonClick?: () => void;
  showSearch?: boolean;
  className?: string;
};

export function NavigationBar({
  title,
  topBar: navItems,
  drawerItems: drawer,
  onSearchBarButtonClick,
  showSearch = false,
  className,
}: Readonly<NavigationBarProps>) {
  const matchRoute = useMatchRoute();
  const navLinkProps = useNavLinkProps();
  const setDrawerOpen = useSetAtom(drawerAtom);
  const sidebarNav = useMarketplaceOverhaul("navigation");
  const halloween = useFeatureFlag("halloween-easter-eggs");

  const renderNavItem = (item: NavItem) => {
    if (item.type === "link") {
      const linkProps = navLinkProps(item);
      const isActive = !!matchRoute({ to: linkProps.to, fuzzy: true });
      return (
        <Link
          key={item.title}
          className={cn(buttonVariants({ icon: true }), "mt-0.5 mr-0.5", isActive && "[&_svg]:text-brand-hover!")}
          {...linkProps}
          target={item.external ? "_blank" : undefined}
        >
          {item.icon}
        </Link>
      );
    }
    if (item.type === "button") {
      return (
        <Button
          key={item.title}
          className="mt-0.5 mr-0.5"
          icon
          onClick={(e) => {
            e.stopPropagation();
            item.onClick();
          }}
        >
          {item.icon}
        </Button>
      );
    }
    return null;
  };

  return (
    <MobileTopNavigation
      leftSlot={
        sidebarNav && (
          <Button icon aria-label="Open menu" className="ml-1.5 text-white" onClick={() => setDrawerOpen(true)}>
            <PanelLeft className="size-4" />
          </Button>
        )
      }
      title={title ?? ""}
      decoration={halloween && <MobileHeaderDecor />}
      rightSlot={
        <div className="flex flex-row text-white [&>a]:text-white">
          {showSearch && (
            <Button className="mt-0.5 mr-0.5" icon onClick={onSearchBarButtonClick}>
              <Search />
            </Button>
          )}
          {navItems.map((item) => renderNavItem(item))}
          {!sidebarNav && !!drawer.length && (
            <Button className="mr-1" icon onClick={() => setDrawerOpen(true)}>
              <Menu />
            </Button>
          )}
        </div>
      }
      className={className}
    />
  );
}
